/**
 * SpreadsheetToolbar.jsx
 * Metadata-driven sticky toolbar.
 * Renders only actions permitted by the active sheet's permission metadata.
 */

import React, { memo, useCallback } from 'react';
import {
  Save, Send, Download, RefreshCw, Plus, Trash2, Lock,
  CheckCircle, AlertCircle, Loader2, Search, Settings, Sun, Moon,
  Maximize2, Minimize2, BarChart2,
} from 'lucide-react';
import { useWorkbookStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';
import { useUiStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';

// ─── Icon map (matches toolbar action metadata icon strings) ──────────────────
const ICONS = {
  save:      Save,
  send:      Send,
  submit:    Send,
  download:  Download,
  export:    Download,
  refresh:   RefreshCw,
  plus:      Plus,
  addRow:    Plus,
  delete:    Trash2,
  approve:   CheckCircle,
  settings:  Settings,
};

// ─── Save Status Chip ─────────────────────────────────────────────────────────
const SaveStatus = memo(({ status }) => {
  const cfgs = {
    saved:   { Icon: CheckCircle, color: '#16a34a', label: 'Saved'    },
    unsaved: { Icon: AlertCircle,  color: '#d97706', label: 'Unsaved'  },
    saving:  { Icon: Loader2,      color: '#2563eb', label: 'Saving…'  },
    error:   { Icon: AlertCircle,  color: '#dc2626', label: 'Error'    },
  };
  const cfg = cfgs[status] || cfgs.saved;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: cfg.color }}>
      <cfg.Icon size={12} style={status === 'saving' ? { animation: 'spin 1s linear infinite' } : {}} />
      <span style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>{cfg.label}</span>
    </div>
  );
});

// ─── Single Toolbar Button ────────────────────────────────────────────────────
const ToolbarButton = memo(({ action, permitted, isSaving, onClick }) => {
  const IconCmp    = ICONS[action.icon] || ICONS[action.id] || BarChart2;
  const isPrimary  = action.type === 'primary';
  const isDisabled = !permitted || isSaving;
  const isLoading  = isSaving && action.id === 'save';

  return (
    <button
      onClick={() => !isDisabled && onClick(action.id)}
      disabled={isDisabled}
      title={!permitted ? 'Permission required' : action.label}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '5px 12px', borderRadius: 5, border: '1px solid',
        fontSize: 11, fontWeight: 600, cursor: isDisabled ? 'not-allowed' : 'pointer',
        fontFamily: "'DM Sans', sans-serif", transition: 'all 0.12s ease',
        whiteSpace: 'nowrap',
        background: isDisabled
          ? '#f9fafb'
          : isPrimary ? '#1a56db' : 'transparent',
        borderColor: isDisabled
          ? '#e5e7eb'
          : isPrimary ? '#1a56db' : '#d1d5db',
        color: isDisabled
          ? '#9ca3af'
          : isPrimary ? '#fff' : '#374151',
        opacity: isSaving && action.id !== 'save' ? 0.6 : 1,
      }}
    >
      {isLoading
        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
        : <IconCmp size={12} />
      }
      {action.label}
      {!permitted && <Lock size={9} style={{ marginLeft: 1 }} />}
    </button>
  );
});

// ─── Divider ──────────────────────────────────────────────────────────────────
const Divider = () => (
  <div style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px', flexShrink: 0 }} />
);

// ─── Main Toolbar ─────────────────────────────────────────────────────────────
const SpreadsheetToolbar = memo(({
  workbookName,
  sheetName,
  toolbarActions,   // from metadata
  permissions,      // from metadata
  onAction,         // (actionId) => void
  errorCount,
  dirtyCount,
}) => {
  const { saveStatus, isSaving }    = useWorkbookStore();
  const { searchText, setSearchText, theme, toggleTheme, isFullscreen, toggleFullscreen } = useUiStore();

  const handleSearch = useCallback((e) => setSearchText(e.target.value), [setSearchText]);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '6px 14px',
      background: '#ffffff',
      borderBottom: '2px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      position: 'sticky', top: 0, zIndex: 200,
      gap: 12,
    }}>

      {/* Left: Logo + breadcrumb + save status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          paddingRight: 10, borderRight: '1px solid #e2e8f0',
        }}>
          <div style={{
            width: 24, height: 24, borderRadius: 5, background: 'linear-gradient(135deg, #1a56db 0%, #3b82f6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <BarChart2 size={13} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', lineHeight: 1.2, fontFamily: "'DM Serif Display', serif" }}>
              {workbookName}
            </div>
            <div style={{ fontSize: 9, color: '#6b7280', lineHeight: 1 }}>
              {sheetName}
            </div>
          </div>
        </div>

        <SaveStatus status={isSaving ? 'saving' : saveStatus} />

        {/* Error/dirty badges */}
        {errorCount > 0 && (
          <span style={{ ...chipStyle, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>
            <AlertCircle size={9} /> {errorCount} error{errorCount > 1 ? 's' : ''}
          </span>
        )}
        {dirtyCount > 0 && (
          <span style={{ ...chipStyle, background: '#fffbeb', color: '#d97706', border: '1px solid #fcd34d' }}>
            {dirtyCount} unsaved
          </span>
        )}
      </div>

      {/* Center: Metadata-driven action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
        {toolbarActions.map((action, idx) => {
          const permitted = action.permission ? (permissions[action.permission] !== false) : true;
          return (
            <React.Fragment key={action.id}>
              {action.dividerBefore && <Divider />}
              <ToolbarButton
                action={action}
                permitted={permitted}
                isSaving={isSaving}
                onClick={onAction}
              />
            </React.Fragment>
          );
        })}
      </div>

      {/* Right: Search + utility buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <Search size={11} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            value={searchText}
            onChange={handleSearch}
            placeholder="Search…"
            style={{
              paddingLeft: 24, paddingRight: 8, height: 28, width: 180,
              border: '1px solid #e2e8f0', borderRadius: 5, fontSize: 11,
              background: '#f8fafc', outline: 'none',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>

        <Divider />

        {/* Theme toggle */}
        <button onClick={toggleTheme} style={utilBtnStyle} title="Toggle theme">
          {theme === 'light' ? <Moon size={13} color="#6b7280" /> : <Sun size={13} color="#6b7280" />}
        </button>

        {/* Fullscreen toggle */}
        <button onClick={toggleFullscreen} style={utilBtnStyle} title="Toggle fullscreen">
          {isFullscreen ? <Minimize2 size={13} color="#6b7280" /> : <Maximize2 size={13} color="#6b7280" />}
        </button>
      </div>
    </div>
  );
});

const chipStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 3,
  padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif",
};

const utilBtnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 5,
  background: 'transparent', cursor: 'pointer', padding: 0,
};

export default SpreadsheetToolbar;
